import {ExclamationIcon, InformationCircleIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {Button, Modal, TextArea} from '@/components';
import type {AdminPinResetRequest} from '@/types';
import {BusinessPinResetRequestStatus} from '@/types';

type PinResetDecisionModalProps = {
  isOpen: boolean;
  request: AdminPinResetRequest | null;
  status: BusinessPinResetRequestStatus | null;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (note?: string) => Promise<void>;
};

/**
 * Records the operator's decision. Approval is a plain confirm: it needs no
 * note, only the warning that it clears the account's PIN. Declining keeps an
 * optional note, which the rejection email passes on to the business.
 */
export const PinResetDecisionModal = ({
  isOpen,
  request,
  status,
  isSubmitting,
  onClose,
  onConfirm,
}: PinResetDecisionModalProps) => {
  const [note, setNote] = useState('');
  const isApproval = status === BusinessPinResetRequestStatus.Approved;
  const business = request?.businessName || request?.businessId || '';
  const decisionSummary = isApproval
    ? `Approving clears the transaction PIN on ${business}. The account's personnel are emailed, and the account is asked to create a new PIN.`
    : `Declining keeps the transaction PIN on ${business} unchanged. The account's personnel are emailed the decision.`;

  const closeModal = () => {
    setNote('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      title={isApproval ? 'Approve PIN reset' : 'Decline PIN reset'}
      width="w-[32rem]"
      dataCy="pin-reset-decision-modal"
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          // The note field only exists when declining, so an approval never
          // carries a note typed for an earlier decline.
          onConfirm(isApproval ? undefined : note.trim() || undefined).catch(
            () => {},
          );
        }}
      >
        <div
          role="alert"
          className={`flex gap-3 rounded-md border p-3 text-sm ${
            isApproval
              ? 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100'
              : 'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100'
          }`}
        >
          {isApproval ? (
            <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          ) : (
            <ExclamationIcon className="mt-0.5 h-5 w-5 shrink-0" />
          )}
          <div>
            <p className="font-medium">
              {isApproval
                ? 'This clears the account PIN'
                : 'The PIN stays as it is'}
            </p>
            <p className="mt-1">{decisionSummary}</p>
          </div>
        </div>
        {request?.reason ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Reason given: {request.reason}
          </p>
        ) : null}
        {isApproval ? null : (
          <TextArea
            id="pin-reset-decision-note"
            label="Reason for declining (optional)"
            name="note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={500}
            aria-rowspan={3}
            className="w-full isFull"
            data-cy="pin-reset-decision-note"
          />
        )}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={closeModal}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme={isApproval ? 'green' : 'red'}
            isLoading={isSubmitting}
            loadingText={isApproval ? 'Approving...' : 'Declining...'}
            dataCy="pin-reset-decision-submit"
          >
            {isApproval ? 'Approve and clear PIN' : 'Decline request'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
